import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { POSTING_DISCOVERY_QUERY } from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import { FilterId } from '../../filters/types/filters';
import type { MapPoint } from '../Chart/Map/types';
import { buildPostingDiscoveryFilters } from './postingDiscovery/build-filters';
import type { PostingRow } from './postingDiscovery/types';
import {
  type DataProviderChildren,
  DataProviderQueryStatus,
  type DataProviderSet,
  DataType,
} from './types';
import { transformPostingDiscoveryMapData } from './utils/posting-discovery-map-utils';

type PostingsDiscoveryData = {
  tableData?: DataProviderSet;
  mapData?: { points: MapPoint[] };
};

type PostingDiscoveryDataProviderProps = {
  children: DataProviderChildren<PostingsDiscoveryData | undefined>;
  exchangeRate?: number;
};

export const PostingDiscoveryDataProvider = ({
  children,
  exchangeRate,
}: PostingDiscoveryDataProviderProps) => {
  const filters = useEffectiveFilters();

  const variables = buildPostingDiscoveryFilters(filters);
  const hasFilters = (() => {
    const expr = variables.filters.expr;

    if (!expr) return false;

    // Check each UserNode type
    if (expr.posting_and) {
      return expr.posting_and.children.length > 0;
    }

    if (expr.posting_or) {
      return expr.posting_or.children.length > 0;
    }

    if (expr.posting_value) {
      return true;
    }

    if (expr.posting_not) {
      return true;
    }

    return false;
  })();

  const { customSkillTree } = useCustomSkillTaxonomyTree();
  const hasPotentialSkillFilters = filters.some(
    (f) =>
      f.id === FilterId.CUSTOM_SKILL ||
      f.id === FilterId.MULTI_CUSTOM_SKILL ||
      f.id === FilterId.SKILL_TAXONOMY
  );

  const isWaitingForSkillsTaxonomy =
    !isEmpty(filters) && customSkillTree === null && hasPotentialSkillFilters;
  const [{ data, fetching }] = useQuery({
    query: POSTING_DISCOVERY_QUERY,
    variables: { query: variables },
    pause: !hasFilters || isWaitingForSkillsTaxonomy,
  });

  // Convert salary to the user's preferred currency client-side (matches the
  // pattern in TalentDiscoveryDataProvider). The table/card/popout all render
  // from this same array, so converting once here covers every salary surface.
  // Memoized so an unrelated re-render doesn't hand consumers a fresh array ref.
  const postings = useMemo(() => {
    const rawPostings = data?.posting_discovery_search?.postings ?? [];
    if (!exchangeRate || exchangeRate === 1) {
      return rawPostings;
    }
    return rawPostings.map((posting) =>
      typeof posting.salary === 'number'
        ? { ...posting, salary: posting.salary * exchangeRate }
        : posting
    );
  }, [data?.posting_discovery_search?.postings, exchangeRate]);

  const mapPoints = transformPostingDiscoveryMapData(
    data?.posting_discovery_search?.map_data ?? []
  );

  const status = (() => {
    if (fetching) {
      return DataProviderQueryStatus.Fetching;
    }
    if (data !== undefined && data !== null) {
      return DataProviderQueryStatus.Success;
    }
    return DataProviderQueryStatus.Idle;
  })();

  if (isWaitingForSkillsTaxonomy) {
    return children({
      data: { tableData: undefined, mapData: undefined },
      status: DataProviderQueryStatus.Fetching,
    });
  }

  if (!hasFilters) {
    return children({
      data: { tableData: undefined, mapData: undefined },
      status: DataProviderQueryStatus.Success,
    });
  }

  return children({
    data: {
      tableData: {
        type: DataType.TABLE_ROWS,
        data: postings,
      },
      mapData: { points: mapPoints },
    },
    status,
  });
};

export type { PostingRow };
